import type { CustomCellEditorProps } from 'ag-grid-react';
import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Checkbox, TruncateText } from '@mint-lib/tw-components';
import {
  ComboboxGroup,
  ComboboxItem,
  ComboboxMenu,
  ComboboxMenuButton,
} from '@mint-lib/tw-components/patterns';

export type GroupedSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type SelectOptionGroup = {
  key: string;
  label: string;
  options: GroupedSelectOption[];
};

export interface GroupedSelectEditorProps extends CustomCellEditorProps<
  unknown,
  string | string[]
> {
  /** Selection type, single or multiple */
  type?: 'single' | 'multiple';

  /** Enable search input */
  withSearch?: boolean;

  /** Show Select all / Clear all actions (multiple mode only) */
  withSelectAll?: boolean;

  /** Pre-grouped options to render */
  groups?: SelectOptionGroup[];

  /** Callback when an option is selected, provides the option and its group */
  onOptionSelect?: (
    option: GroupedSelectOption,
    group: SelectOptionGroup,
  ) => void;
}

export const GroupedSelectEditor = ({
  type = 'single',
  withSearch = true,
  withSelectAll = true,
  groups = [],
  value,
  onValueChange,
  onOptionSelect,
  api,
}: GroupedSelectEditorProps) => {
  const { t } = useTranslation('@mint-lib/ag-grid');

  const selected = useMemo(() => {
    if (!value) return new Set<string>();
    return new Set(Array.isArray(value) ? value : [value]);
  }, [value]);

  const allOptions = useMemo(
    () => groups.flatMap((group) => group.options),
    [groups],
  );

  const handleSingleSelect = (
    option: GroupedSelectOption,
    group: SelectOptionGroup,
  ) => {
    if (option.disabled) return;
    onValueChange(option.value);
    onOptionSelect?.(option, group);
    api?.stopEditing?.();
  };

  const handleMultipleSelect = (
    option: GroupedSelectOption,
    group: SelectOptionGroup,
  ) => {
    if (option.disabled) return;
    const newSelected = new Set(selected);
    if (newSelected.has(option.value)) {
      newSelected.delete(option.value);
    } else {
      newSelected.add(option.value);
    }
    onValueChange(Array.from(newSelected));
    onOptionSelect?.(option, group);
  };

  const handleSelectAll = () => {
    onValueChange(
      allOptions
        .filter((option) => !option.disabled || selected.has(option.value))
        .map((option) => option.value),
    );
  };

  const handleClearAll = () => {
    onValueChange([]);
  };

  return (
    <div className="max-w-150 bg-ui-primary">
      <ComboboxMenu
        withSearch={withSearch}
        emptyMessage={t('No options available')}
      >
        {type === 'multiple' && withSelectAll && (
          <ComboboxMenuButton
            selectedOptions={Array.from(selected)}
            options={allOptions}
            onSelectAll={handleSelectAll}
            onClearAll={handleClearAll}
          />
        )}
        {groups.map((group, index) => (
          <ComboboxGroup key={group.key} header={group.label}>
            {group.options.map((option) => {
              const isSelected = selected.has(option.value);

              return (
                <ComboboxItem
                  key={option.value}
                  value={`${group.label} ${option.label}`}
                  selected={isSelected}
                  disabled={option.disabled}
                  onSelect={() =>
                    type === 'single'
                      ? handleSingleSelect(option, group)
                      : handleMultipleSelect(option, group)
                  }
                  data-dd-action-name="Grid cell grouped select: option"
                >
                  {type === 'multiple' && <Checkbox checked={isSelected} />}
                  <TruncateText tooltipClassName="break-all whitespace-normal">
                    {option.label}
                  </TruncateText>
                </ComboboxItem>
              );
            })}
          </ComboboxGroup>
        ))}
      </ComboboxMenu>
    </div>
  );
};
